// src/tattoo/autosave.js

import {
  getProjectJSON,
  isDirty as isDirtyState,
  loadProjectFromData,
  markSaved as markSavedState,
} from "./state.js";
import { upsertRecentProject } from "./projectsIndex";
import { getCurrentProjectId } from "./state";

// ✅ NEW: IndexedDB autosave backend
import { idbPut, idbGet, idbPrune, IDB_KEYS } from "./idbAutosave.js";

let autosaveDisabledForQuota = false;

// Keep this key for legacy restore only
export const AUTOSAVE_KEY = "tt_autosave_project";

// ✅ NEW: small pointer only
const AUTOSAVE_LAST_ID_KEY = "tt_autosave_last_project_id";

const AUTOSAVE_MS = 5000;

export function markSaved(tag = "manual") {
  try {
    console.log(
      `[autosave] markSaved (${tag}) @ ${new Date().toLocaleTimeString()}`
    );
  } catch {}
  markSavedState(tag);
}

export function markJustSaved() {
  markSaved("just-saved");
  try {
    window.dispatchEvent(
      new CustomEvent("tattoo:just-saved", { detail: { type: "manual" } })
    );
  } catch (e) {
    console.warn("[autosave] could not dispatch just-saved event", e);
  }
  try {
    syncAutosaveCache?.();
  } catch {}
}

/**
 * Legacy helper – keep it so nothing breaks.
 * Now tries IDB first, then falls back to old localStorage snapshot.
 */
export async function tryRestoreAutosave() {
  return await restoreAutosaveIfAvailable();
}

export function enableAutosave() {
  let inFlight = false;

  let lastWrittenId = null;

  async function tick() {
    console.log("[autosave] TICK", {
      paused: !!window.__tt_pause_autosave,
      disabledForQuota: !!autosaveDisabledForQuota,
      inFlight,
      dirty: (() => {
        try {
          return isDirtyState();
        } catch {
          return "err";
        }
      })(),
    });
    if (autosaveDisabledForQuota) return;
    if (inFlight) return;
    inFlight = true;

    try {
      // only save when dirty
      try {
        if (!isDirtyState()) return;
      } catch {}

      let json;
      try {
        json = getProjectJSON();
      } catch (e) {
        console.warn("[autosave] failed to build project JSON:", e);
        return;
      }

      let data;
      try {
        data = JSON.parse(json);
        console.log("[autosave] DATA CHECK", {
          projectId: data?.projectId,
          hasMorphs: !!data?.morphs,
          morphKeys: data?.morphs ? Object.keys(data.morphs).length : 0,
          appearance: { ...tattoo.appearance },
          hasViewState: !!data?.viewState,
          viewStateAt: data?.viewState?.at ?? null,
        });
      } catch (e) {
        console.warn("[autosave] JSON parse failed:", e);
        return;
      }

      const projectId =
        (typeof data.projectId === "string" && data.projectId.trim()) ||
        getCurrentProjectId();
      if (!projectId) return;

      let projectName =
        (data.projectName && data.projectName.trim()) || "";
      const sex = data.sex || "female";

      // 🛡️ HARD GUARD against reset-state corruption.
      //
      // Autosave sometimes fires when in-memory state has been partially
      // or fully reset (races between handleNewSession / restore / view
      // changes). If we blindly write that reset state to a slot that
      // already contains a real project, we permanently destroy the
      // user's work — decals disappear, names revert to defaults.
      //
      // Rule: if what we're about to write looks like a "fresh reset"
      // (no layers AND no name) AND the target slot already has real
      // content on disk, skip the write entirely. There is NO legitimate
      // scenario where a saved project simultaneously has real content
      // on disk and default-empty in-memory state. A user who genuinely
      // deletes all layers still has a projectName (name persists
      // through layer deletion) — so we require BOTH signals before
      // triggering the skip.
      const inMemoryIsResetLike =
        (!Array.isArray(data.layers) || data.layers.length === 0) &&
        !projectName;

      // 🛡️ ABSOLUTE guard: never persist a completely unedited fresh
      // reset state. Either the user has named the project OR they've
      // added at least one layer — that's the minimum "user has done
      // something meaningful" bar. Empty-empty is always either a
      // corruption in flight OR an orphan "+ Start a New Session" that
      // the user abandoned without editing, and either way it should
      // never end up in recents as an "Untitled" / "Tattune" phantom.
      if (inMemoryIsResetLike) {
        console.warn(
          "[autosave] REFUSING WRITE — empty-empty state (no name, no layers) is never worth persisting",
          {
            projectId,
            incomingLayers: data.layers?.length ?? 0,
            incomingName: projectName || "(empty)",
          }
        );
        return; // do not write ANY empty-empty snapshot
      }

      // Softer projectName preservation for the "empty name only" case
      // (layers present but name blank — could be a hiccup mid-load).
      if (!projectName) {
        try {
          const existing = await idbGet(projectId);
          const preserved =
            existing?.projectName && existing.projectName.trim();
          if (preserved) {
            projectName = preserved;
            data.projectName = preserved;
            json = JSON.stringify(data);
          } else {
            projectName = "Untitled";
          }
        } catch {
          projectName = "Untitled";
        }
      }

      // 1) BIG snapshot to IDB
      console.log("[autosave] about to idbPut", {
        projectId,
        bytes: json.length,
      });
      await idbPut(projectId, { projectId, projectName, sex, json });
      console.log("[autosave] idbPut OK", projectId);

      // 2) tiny pointer in localStorage
      // 2) tiny pointer in localStorage
      try {
        localStorage.setItem(AUTOSAVE_LAST_ID_KEY, projectId);
      } catch {}

      // 3) pointer in IDB (only when changed)
      try {
        if (lastWrittenId !== projectId) {
          await idbPut(IDB_KEYS.LAST_ID, { projectId });
          lastWrittenId = projectId;
        }
      } catch {}

      // 4) prune occasionally
      try {
        enableAutosave._pruneCounter = (enableAutosave._pruneCounter || 0) + 1;
        if (enableAutosave._pruneCounter % 12 === 0) {
          await idbPrune({ keep: 12 });
        }
      } catch {}

      console.log("[autosave] wrote snapshot to IDB", json.length, "bytes");
      markSaved("autosave");

      // Recents intentionally NOT updated from autosave — that write path
      // has been the source of "phantom Tattune" entries. Recents get
      // updated exclusively via captureAndIndexPreview (on dashboard
      // entry), which already has an empty-empty guard preventing
      // orphan slot entries. Autosave's job is to persist the snapshot;
      // recents metadata is a separate concern with different lifecycle
      // rules.
    } catch (e) {
      console.warn("[autosave] tick failed:", e);
    } finally {
      inFlight = false;
    }
  }

  const id = setInterval(() => {
    tick(); // fire-and-forget; tick handles errors
  }, AUTOSAVE_MS);

  tick(); // run once quickly
  console.log("[autosave] enabled (IDB)");
  return () => {
    clearInterval(id);
    console.log("[autosave] disabled");
  };
}

export function installBeforeUnload() {
  const handler = (e) => {
    try {
      if (!isDirtyState()) return;
    } catch {}
    e.preventDefault();
    e.returnValue =
      "Unsaved changes will be lost. Save your project before leaving.";
    return e.returnValue;
  };
  window.addEventListener("beforeunload", handler);
  console.log("[autosave] beforeunload installed");
  return () => {
    window.removeEventListener("beforeunload", handler);
    console.log("[autosave] beforeunload removed");
  };
}

/**
 * ✅ Restores from IndexedDB (preferred).
 * Falls back to legacy localStorage snapshot for older sessions.
 */
export async function restoreAutosaveIfAvailable() {
  // 1) Try to find last autosaved project id
  let lastId = null;

  try {
    lastId = localStorage.getItem(AUTOSAVE_LAST_ID_KEY) || null;
  } catch {}

  if (!lastId) {
    try {
      const ptr = await idbGet(IDB_KEYS.LAST_ID);
      lastId = ptr?.projectId || null;
    } catch {}
  }

  // 2) Try IDB snapshot
  if (lastId) {
    try {
      const rec = await idbGet(lastId);
      const json = rec?.json;
      if (json && typeof json === "string") {
        const data = JSON.parse(json);
        // Pass lastId as the explicit slot — the pointer told us which
        // slot to restore, so it's the source of truth even if the
        // snapshot's internal projectId is missing.
        loadProjectFromData(data, lastId);
        markSaved("autosave-restore");
        console.log("[autosave] restored project from IndexedDB", lastId);
        console.log(
          "[restore] loaded appearance from snapshot",
          data.appearance
        );
        console.log("[restore] tattoo.appearance AFTER apply", {
          ...tattoo.appearance,
        });
        return true;
      }
    } catch (err) {
      console.warn("[autosave] IDB restore failed:", err);
    }
  }

  // 3) Legacy fallback: localStorage snapshot
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return false;

    const data = JSON.parse(raw);
    loadProjectFromData(data);
    markSaved("autosave-restore-legacy");
    console.log("[autosave] restored project from legacy localStorage");
    return true;
  } catch (err) {
    console.warn("[autosave] legacy restore failed:", err);
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
    } catch {}
    return false;
  }
}
